/**
 * Monotonic status merge utilities for the workflow stepper.
 *
 * Both ora and the Outlook add-in use the same primitives so the step
 * indicator never regresses: whichever source (HTTP poll or SignalR push)
 * reports further progress wins, and the merge is idempotent.
 *
 * Import paths:
 *   ora    → ~/common/workflows/statusMerge
 *   add-in → @product-app/common/workflows/statusMerge
 */

import type { InstanceStatusSnapshot, WorkflowEvent } from "./types";
import {
  FINISHED_STATUSES,
  needsAcknowledgementOf,
  readWarningItems,
  unionWarningItems,
  type RunWarningItem,
} from "./warningAcknowledgement";

// ---------------------------------------------------------------------------
// Terminal status helpers
// ---------------------------------------------------------------------------

/** One definition: warningAcknowledgement's FINISHED_STATUSES (statusMerge already imports that file). */
export const TERMINAL_STATUSES = FINISHED_STATUSES;

export const isTerminal = (s?: string | null): boolean =>
  !!s && TERMINAL_STATUSES.has(s.toLowerCase());

// ---------------------------------------------------------------------------
// Cancel eligibility — MUST mirror the substrate's CANCELLABLE_STATUSES
// (workflows/src/workflows/tools/resume_cancel.py). A UI cancel affordance
// that doesn't match the backend either (a) hides cancel for a status the
// backend WOULD cancel — e.g. `pending_email_send`, a cold hand-off waiting
// on the AM to send the reply, which then permanently blocks re-launching
// that email via the concurrent-run guard — or (b) offers a cancel the
// backend rejects (e.g. `pending_edocs`, deliberately NOT cancellable). Keep
// this set in lockstep with the backend so neither drifts.
export const CANCELLABLE_STATUSES = new Set([
  "running",
  "paused_hitl",
  "warm_hitl",
  "paused",
  "pending_email_send",
]);

export const isCancellable = (s?: string | null): boolean =>
  !!s && CANCELLABLE_STATUSES.has(s.toLowerCase());

// ---------------------------------------------------------------------------
// Outcome-bearing fields (Ruling R38)
// ---------------------------------------------------------------------------

/**
 * The status-payload fields the step view reads to decide whether it may claim
 * success. They ride ONLY the REST status payload (never a SignalR event), and
 * are only meaningful once the run is terminal or parked at
 * `pending_email_send`. Carrying the earlier `running` snapshot's values
 * (`ended_early:false`, `result_warnings:[]`) across that transition is how a
 * live-watched run came to read as clean.
 */
const OUTCOME_KEYS = [
  "ended_early",
  "early_exit_reason",
  "result_warnings",
  "has_warnings",
] as const;

/** A status after which the step view makes outcome claims. */
const isOutcomeBearing = (s?: string | null): boolean =>
  isTerminal(s) || s === "pending_email_send";

const carriesOutcome = (s: object): boolean => {
  const r = s as Record<string, unknown>;
  return r.ended_early !== undefined || r.result_warnings !== undefined;
};

// ---------------------------------------------------------------------------
// Warning-acknowledgement fields (Ruling R50, 2026-10-06)
// ---------------------------------------------------------------------------

/**
 * NOT outcome fields. They change while a run STAYS `completed` (a person
 * acknowledges a line), so the "same status, server first" rule above would let a
 * stale `completed` read overwrite a fresher one; and no timestamp can order two
 * such reads, because acknowledging writes `workflow_warning_acknowledgements`,
 * never `workflow_instances.updated_at`.
 *
 * The rule instead:
 *  - lines come from the snapshots that share the winner's status — a run's
 *    lines can change only at a status transition;
 *  - an acknowledgement in ANY of them stands (per-line monotonic union) — safe
 *    because the server never deletes one and records only the first acknowledger,
 *    so every snapshot that says "acknowledged" says the same thing;
 *  - `needs_acknowledgement` is re-derived from the merged lines, never copied.
 * Once ONE same-status read's `warning_items` is present but unreadable, the merged
 * state stays "unknown" for the rest of the session (the raw array is kept and
 * flagged again on every merge). Deliberate and fail-safe (R38 round 3).
 * No same-status snapshot with lines → all three keys absent ("unknown"), which
 * every surface renders as before the feature. Optimistic state never reaches a
 * snapshot (the hook keeps it apart), so nothing here can claim an acknowledgement
 * the server has not confirmed.
 */
const ACK_KEYS = ["warning_items", "needs_acknowledgement", "can_acknowledge"] as const;

const ACK_UNKNOWN: Record<string, unknown> = Object.fromEntries(
  ACK_KEYS.map((k) => [k, undefined]),
);

function mergeAckFields(
  sources: readonly object[],
  status: string | null | undefined,
  winner?: object,
): Record<string, unknown> {
  const same = sources.filter(
    (s) => (s as { status?: unknown }).status === status,
  ) as Record<string, unknown>[];
  const read = same.map((s) => ({ s, items: readWarningItems(s) }));
  // A same-status read whose list is PRESENT but unreadable makes the state
  // unknown: dropping it would let a stale valid list claim "all acknowledged".
  // The RAW array is kept (every reader still sees "unknown"), so the step view's
  // raw-array check keeps firing and the next merge flags it unreadable again;
  // wiping it would read as a clean Done from the second poll on.
  const unreadable = read.filter(({ s, items }) => items === null && Array.isArray(s.warning_items));
  if (unreadable.length > 0) {
    const pick =
      unreadable.find(({ s }) => s === winner) ??
      unreadable.find(({ s }) => (s.warning_items as unknown[]).length > 0) ??
      unreadable[0];
    return {
      warning_items: pick.s.warning_items,
      needs_acknowledgement: undefined,
      can_acknowledge: undefined,
    };
  }
  const lists = read
    .map(({ items }) => items)
    .filter((l): l is RunWarningItem[] => l !== null);
  const merged = unionWarningItems(lists);
  if (merged === null) return ACK_UNKNOWN;
  const can = same.find((s) => typeof s.can_acknowledge === "boolean")?.can_acknowledge;
  return {
    warning_items: merged,
    needs_acknowledgement: needsAcknowledgementOf(merged),
    can_acknowledge: can === true,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Numeric max treating null/undefined as -Infinity (never beats a real value). */
function maxNum(
  a: number | null | undefined,
  b: number | null | undefined,
): number | null {
  const aVal = a ?? null;
  const bVal = b ?? null;
  if (aVal === null && bVal === null) return null;
  if (aVal === null) return bVal;
  if (bVal === null) return aVal;
  return Math.max(aVal, bVal);
}

// HITL-style statuses: any state where the substrate is waiting on a human
// response and has a live `hitl_question` payload attached. Keep in sync with
// `pickDrawerStatusFamily` and `getSectionOrder` — those three places are the
// canonical "is this a HITL state for the UI?" check. `rpa_review` is the
// substrate's review-checkpoint variant (operator confirms what an RPA leg
// just did). `pending_edocs` is the cold edoc_wait gate: it needs no AM
// decision but DOES carry a (reject-only) question whose x-actions render the
// "Check Now" button — without it here, `mergeStatus` treats the pending_edocs
// winner as non-HITL and silently clears `hitl_question`, so the drawer panel
// flashes in on the first poll and then collapses on the next (the
// flash-then-disappear symptom).
const HITL_STATUSES = new Set<string>([
  "paused_hitl",
  "warm_hitl",
  "rpa_review",
  "pending_edocs",
]);

export const isHitlStatus = (s?: string | null): boolean =>
  !!s && HITL_STATUSES.has(s);

// Internal alias kept so existing call sites compile without churn.
const isHitl = isHitlStatus;

/**
 * Pick the "winner" source between two snapshots.
 *
 * Rules (in priority order):
 * 1. Terminal beats non-terminal.
 * 2. Both non-terminal AND both have updated_at AND timestamps differ →
 *    newer timestamp wins. The substrate stamps every state transition with
 *    a fresh updated_at, so a strictly-newer response is the authoritative
 *    state — regardless of HITL vs non-HITL. This is critical for
 *    cross-surface transitions: when one user approves a warm_hitl in ora,
 *    the plugin's next poll returns "running" with a newer timestamp, and
 *    we MUST trust it instead of clinging to the local warm_hitl cache.
 * 3. Timestamps tie or are missing → HITL beats non-HITL. Race protection
 *    for the case where a stale "running" SignalR event (sometimes emitted
 *    BEFORE the substrate persists the next warm_hitl row) would otherwise
 *    silence a fresh HITL poll. Step_index in HTTP-derived snapshots and
 *    SignalR-emitted events can diverge by 1 in this race, and the rule
 *    ensures we don't drop the HITL question on the floor.
 * 4. Higher step_index wins.
 * 5. Final lexicographic updated_at tiebreak (ISO-8601 sorts correctly).
 */
function pickWinner<T extends InstanceStatusSnapshot & { updated_at?: string | null }>(
  a: T,
  b: T,
): "a" | "b" {
  const aTerminal = isTerminal(a.status);
  const bTerminal = isTerminal(b.status);
  if (aTerminal && !bTerminal) return "a";
  if (bTerminal && !aTerminal) return "b";

  // Both non-terminal: trust the newer timestamp as the authoritative source.
  // ISO-8601 strings sort lexicographically as dates, so direct string
  // comparison is correct.
  const aTs = a.updated_at ?? "";
  const bTs = b.updated_at ?? "";
  if (aTs && bTs && aTs !== bTs) {
    return aTs > bTs ? "a" : "b";
  }

  // Timestamps tie or are missing — fall back to HITL-beats-non-HITL race
  // protection (see rule #3 in the doc above).
  const aHitl = isHitl(a.status);
  const bHitl = isHitl(b.status);
  if (aHitl && !bHitl) return "a";
  if (bHitl && !aHitl) return "b";

  const aIdx = a.step_index ?? -1;
  const bIdx = b.step_index ?? -1;
  if (aIdx > bIdx) return "a";
  if (bIdx > aIdx) return "b";

  return aTs >= bTs ? "a" : "b";
}

/**
 * Choose between two append-only history arrays (`chronology`, `phases`).
 *
 * These deliberately do NOT follow `pickWinner`, and that exemption is the fix
 * for a freeze that made the drawer's Steps list useless:
 *
 *   `pickWinner` prefers the newer `updated_at`. The push fold sets
 *   `updated_at = evt.timestamp` (applyEventToStatus), a CLIENT-side event
 *   clock, while the server's value is the DB row's. In `emit_push` the row is
 *   written BEFORE the wire message is stamped, so `evt.timestamp` is always
 *   strictly later than the `updated_at` the next poll reports. Local state
 *   therefore wins every comparison after the first push — and since history
 *   rides only the REST poll (the SignalR delta carries no chronology at all),
 *   the fresh history was discarded on every poll and the Steps list stayed
 *   pinned to the first snapshot of the session. Status kept updating because
 *   it rides the delta and never faces this comparison.
 *
 * A POINTER (status, phase_model, current_phase) can legitimately move
 * backwards, so it needs the winner guard — and that guard is load-bearing: it
 * stops a slow poll response, already in flight when a transition landed, from
 * reverting the UI. HISTORY cannot move backwards. The substrate only appends
 * rows and fills in `ended_at`, so a comparison buys nothing and, here, cost
 * everything.
 *
 * Longer wins, ties go to `b`. Length alone is not sufficient — the last row
 * gaining its `ended_at` leaves the length unchanged — which is exactly why
 * ties resolve to `b`, the fresher server snapshot at the sole call site
 * (`useInstancePush`'s `mergeStatus(prevSnapshot, next)`). `a` only wins when
 * it is STRICTLY longer, which is a safety net against a truncated or partial
 * response, never the normal path.
 *
 * `null` ("this run reports no history") and `[]` ("none recorded yet") are
 * different statements and both are preserved: a null side simply yields the
 * other. Note `[] ?? rows` would have yielded `[]` under the old rule — that is
 * the launch case, where the first poll lands before any phase-history row
 * exists and the log then reads "No history recorded yet" for the entire run.
 */
function pickHistory<E>(
  a: E[] | null | undefined,
  b: E[] | null | undefined,
): E[] | null {
  if (a == null) return b ?? null;
  if (b == null) return a;
  return a.length > b.length ? a : b;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Merge two instance status snapshots monotonically.
 *
 * - step_index and total_steps are always Math.max (never regress).
 * - status, current_phase, hitl_question, error follow the winner (the source
 *   that is terminal or further along).
 * - Heavy/immutable fields (inputs, metadata, workflow_id, etc.) are merged
 *   with null-coalescing so a push delta (which omits these) doesn't blank them.
 *
 * Generic over T so callers keep their concrete type (WorkflowStatusResponse,
 * InstanceStatus, etc.) without losing fields.
 */
export function mergeStatus<
  T extends InstanceStatusSnapshot & { updated_at?: string | null },
>(a: T | null, b: T | null): T | null {
  if (!a) return b;
  if (!b) return a;

  const winner = pickWinner(a, b);
  const w = winner === "a" ? a : b;
  const l = winner === "a" ? b : a; // loser — used for null-coalescing heavy fields

  // Outcome fields (R38): when the winner is outcome-bearing they come from a
  // snapshot with the WINNER'S OWN status that carries them (the server's, `b`
  // first), whatever the timestamps say — never from a stale running or
  // pending_email_send snapshot (the fields really change at that transition),
  // and never from a client-side fold that could not know them. If no such
  // snapshot exists they are left absent and the step view fails safe.
  const outcomeOverride: Record<string, unknown> = {};
  if (isOutcomeBearing(w.status)) {
    const source = [b, a].find(
      (s) => s.status === w.status && carriesOutcome(s),
    ) as Record<string, unknown> | undefined;
    for (const k of OUTCOME_KEYS) outcomeOverride[k] = source?.[k];
  }

  return {
    // Heavy/immutable fields: prefer winner, fall back to loser if winner has null.
    ...l,
    ...w,
    ...outcomeOverride,
    // R50: warning lines from same-status snapshots, acknowledgements unioned.
    ...mergeAckFields([b, a], w.status, w),
    // Monotonic fields: always max regardless of winner.
    step_index: maxNum(a.step_index, b.step_index),
    total_steps: maxNum(a.total_steps, b.total_steps),
    // hitl_question handling:
    //  - Winner non-HITL (running / waiting / etc.): clear. The workflow has
    //    transitioned past the HITL gate; keeping the prior question would
    //    leave the input panel mounted with stale content (cross-surface
    //    approval bug).
    //  - Winner HITL: prefer winner's question, fall back to loser's if the
    //    winner's payload omitted it (e.g., a stripped SignalR event that
    //    won on timestamp but doesn't carry the full question body).
    hitl_question: isHitl(w.status)
      ? ((w as Record<string, unknown>).hitl_question
          ?? (l as Record<string, unknown>).hitl_question
          ?? null)
      : null,
    // run-phase-model's three blocks: take the WINNER's when it carries one,
    // otherwise keep the loser's. `{...l, ...w}` alone would let a winner whose
    // key is present-but-undefined BLANK a value the loser still has, and it
    // would also let an older snapshot that lost pickWinner install its
    // pointer — the pointer must never move backwards.
    //
    // `chronology` and `phases` do NOT follow the winner — see pickHistory.
    // They are still never merged element-wise: concatenating two histories is
    // how duplicate rows and false orderings appear. One side is chosen whole.
    phase_model: w.phase_model ?? l.phase_model ?? null,
    chronology: pickHistory(a.chronology, b.chronology),
    phases: pickHistory(a.phases, b.phases),
    // `metadata` and `evidence_decision` do NOT follow the winner either. No
    // push event carries them, so a push fold only ever holds the copy its
    // first fetch had — and the fold's event-time `updated_at` beats the poll.
    // The capability recorded mid-run (story 28633) then never reached a
    // drawer opened before it: ora said "Awaiting carrier eDocs" while a
    // freshly opened add-in said "Waiting for your documents" (local c5265c53).
    // The server snapshot (`b`, the caller's fetched `next`) is the newer copy
    // whenever it has the field — a null included, so a dropped decision goes.
    metadata:
      "metadata" in b
        ? (b as Record<string, unknown>).metadata
        : (a as Record<string, unknown>).metadata,
    evidence_decision:
      "evidence_decision" in b ? (b.evidence_decision ?? null) : (a.evidence_decision ?? null),
    // The staleness marker is CLIENT-ONLY and is cleared the moment a snapshot
    // carrying a server pointer wins: the server's pointer is authoritative
    // again. A marker that survived a fresh snapshot would make the adapter
    // re-resolve forever. It is never an input to pickWinner.
    __phase_model_stale: w.phase_model != null ? undefined : w.__phase_model_stale,
  } as T;
}

/**
 * Fold a SignalR WorkflowEvent into an existing snapshot.
 *
 * Special rule: `hitl_question` is only cleared on `status_changed` events.
 * Step events (`step_started` / `step_completed`) that carry `hitl_question: null`
 * must NOT wipe a previously-set question — the AM is still waiting at the
 * HITL gate even while the graph internally advances steps within a phase.
 */
export function applyEventToStatus<
  T extends InstanceStatusSnapshot & { updated_at?: string | null },
>(prev: T | null, evt: WorkflowEvent): T | null {
  // applyEventToStatus is for incremental event application, not winner-selection.
  // Events are always authoritative on persisted-status fields; only step_index
  // and total_steps are monotonic (never regress). mergeStatus (winner-selection)
  // is the right primitive when reconciling two equal-authority sources (poll vs push).
  const base = (prev ?? {}) as Partial<T>;
  const isStatusChange = evt.event_type === "status_changed";
  // R38: a push event never carries the outcome fields, so on a transition to a
  // status that makes outcome claims the carried-over (running-era) values are
  // dropped; the authoritative fetch refills them.
  const dropOutcome = isStatusChange && isOutcomeBearing(evt.status);
  // R50: a status_changed that MOVES the run drops the old status's warning lines
  // (they can change at the transition; the authoritative fetch refills them). One
  // that repeats the current status keeps them, and no event ever carries them, so
  // a fold can neither drop nor invent an acknowledgement.
  const statusMoved = isStatusChange && evt.status != null && evt.status !== base.status;

  return {
    // Spread base first so all heavy/immutable fields (inputs, metadata, etc.) survive.
    ...base,
    ...(dropOutcome
      ? Object.fromEntries(OUTCOME_KEYS.map((k) => [k, undefined]))
      : {}),
    ...(statusMoved ? ACK_UNKNOWN : {}),
    // status: only update from event on status_changed. Step events carry transient
    // strings ("step_started"/"step_completed") that must not overwrite the persisted
    // status tracked in the snapshot.
    status: isStatusChange ? (evt.status ?? base.status ?? "") : (base.status ?? ""),
    current_phase: evt.current_phase ?? base.current_phase ?? null,
    step_id: evt.step_id ?? base.step_id ?? null,
    current_step_display_name:
      (evt as WorkflowEvent & { step_display_name?: string | null }).step_display_name ??
      base.current_step_display_name ??
      null,
    // error: only update on status transitions (step events don't carry error context).
    error: isStatusChange ? (evt.error ?? base.error ?? null) : (base.error ?? null),
    updated_at: evt.timestamp ?? base.updated_at ?? null,
    // Monotonic: step_index and total_steps only ever advance forward.
    step_index: maxNum(base.step_index, evt.step_index),
    total_steps: maxNum(base.total_steps, evt.total_steps),
    // hitl_question: status_changed events are authoritative — the substrate
    // includes hitl_question explicitly on every transition (set on pause,
    // null on resume). Step-progress events don't carry the question and
    // fall into the early-return branch above, which preserves the existing
    // value so the Input Needed panel stays visible during warm-hold step
    // advancement.
    hitl_question: isStatusChange
      ? (evt.hitl_question ?? null)
      : (base.hitl_question ?? null),
    // A push event carries NO phase model (emit_push sends step_id /
    // step_index / total_steps / current_phase only), so the base's block is
    // preserved UNCHANGED — the marker never goes inside the server object.
    phase_model: base.phase_model ?? null,
    chronology: base.chronology ?? null,
    evidence_decision: base.evidence_decision ?? null,
    // AD-PLAN-4: when the delta's step_id is not the step the pointer names,
    // the displayed milestone would freeze between refetches. Mark the wrapper
    // stale so the renderer re-resolves the pointer through the DECLARED
    // manifest parentage instead.
    __phase_model_stale:
      evt.step_id != null && evt.step_id !== (base.phase_model?.step?.id ?? null)
        ? true
        : base.__phase_model_stale,
  } as T;
}

// ---------------------------------------------------------------------------
// Plugin-specific: StepProgressState merge
// ---------------------------------------------------------------------------

/** Minimum shape required for mergeStepProgress — mirrors StepProgressState. */
export interface StepProgressCandidate {
  stepIndex: number;
  totalSteps: number;
  currentStepId: string;
  displayName?: string | null;
  completed: boolean;
}

/**
 * Max-merge two StepProgressState-shaped values so the plugin stepper
 * never regresses when a lagging HTTP poll arrives after a more-advanced
 * SignalR push.
 */
export function mergeStepProgress(
  prev: StepProgressCandidate | null,
  candidate: StepProgressCandidate | null,
): StepProgressCandidate | null {
  if (!prev) return candidate;
  if (!candidate) return prev;

  if (candidate.stepIndex > prev.stepIndex) return candidate;
  if (prev.stepIndex > candidate.stepIndex) return prev;

  // Same index: completed beats not-completed; prefer the one with a display name.
  if (candidate.completed && !prev.completed) return candidate;
  if (prev.completed && !candidate.completed) return prev;
  return candidate.displayName ? candidate : prev;
}

/**
 * Derive a human-readable label for a step, falling back through the chain:
 * displayName → phaseName → stepId
 */
export function stepDisplayLabel(
  displayName: string | null | undefined,
  phaseName: string | null | undefined,
  stepId: string | null | undefined,
): string {
  return displayName || phaseName || stepId || "";
}
